// src/services/reportGenerationService.js
// Service for generating Excel reports
// Demo mode: Uses sample data | Production mode: Queries real data from AWS or Supabase

import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabaseClient';
import * as awsApi from './awsApiService';
import { demoPayerPerformance } from '../data/demoData';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

/**
 * Helper: Calculate days between two dates
 */
const daysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));
};

/**
 * Generate AR Aging Detail Report
 * Shows outstanding claims by age bucket (0-30, 31-60, 61-90, 90+ days)
 */
export const generateARAgingReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = [
        { patient: 'John Smith', claim_id: 'CLM-001', payer: 'Delta Dental', service_date: '2026-01-05', amount: 1250.00, current: 500, days_30: 300, days_60: 250, days_90: 200, total: 1250 },
        { patient: 'Jane Doe', claim_id: 'CLM-002', payer: 'Cigna', service_date: '2026-01-10', amount: 850.00, current: 600, days_30: 150, days_60: 100, days_90: 0, total: 850 },
        { patient: 'Bob Johnson', claim_id: 'CLM-003', payer: 'Aetna', service_date: '2025-12-15', amount: 2100.00, current: 800, days_30: 600, days_60: 400, days_90: 300, total: 2100 },
        { patient: 'Sarah Williams', claim_id: 'CLM-004', payer: 'MetLife', service_date: '2026-01-20', amount: 450.00, current: 450, days_30: 0, days_60: 0, days_90: 0, total: 450 },
        { patient: 'Mike Davis', claim_id: 'CLM-005', payer: 'United', service_date: '2025-11-28', amount: 1800.00, current: 900, days_30: 500, days_60: 250, days_90: 150, total: 1800 },
      ];
    } else if (useAWS) {
      // AWS mode: Get claims from Lambda API
      const result = await awsApi.getClaims(practiceId, { limit: 1000 });
      const claims = result.success ? (result.data || []).filter(c =>
        ['Pending', 'Submitted', 'In Review', 'Appealed'].includes(c.claim_status)
      ) : [];

      const today = new Date();
      reportData = claims.map(claim => {
        const outstanding = (parseFloat(claim.claim_amount) || 0) - (parseFloat(claim.claim_paid_amount) || 0);
        const daysOld = daysBetween(claim.service_date || claim.created_at, today);
        const patientName = claim.patient_first_name && claim.patient_last_name
          ? `${claim.patient_first_name} ${claim.patient_last_name}`
          : claim.patient_name || 'Unknown';

        return {
          patient: patientName,
          claim_id: claim.claim_number || claim.id?.slice(0, 8),
          payer: claim.payer_name || claim.payer || 'Unknown',
          service_date: claim.service_date,
          status: claim.claim_status,
          total_amount: parseFloat(claim.claim_amount) || 0,
          paid_amount: parseFloat(claim.claim_paid_amount) || 0,
          outstanding: outstanding,
          days_outstanding: daysOld,
          bucket: daysOld <= 30 ? '0-30' : daysOld <= 60 ? '31-60' : daysOld <= 90 ? '61-90' : '90+'
        };
      });
    } else {
      // Supabase mode: Get unpaid claims with patient info
      const { data: claims, error } = await supabase
        .from('claims')
        .select(`
          id,
          claim_number,
          service_date,
          claim_amount,
          claim_paid_amount,
          payer,
          claim_status,
          created_at,
          patients (first_name, last_name)
        `)
        .eq('practice_id', practiceId)
        .in('claim_status', ['Pending', 'Submitted', 'In Review', 'Appealed']);

      if (error) throw error;

      const today = new Date();
      reportData = (claims || []).map(claim => {
        const outstanding = (parseFloat(claim.claim_amount) || 0) - (parseFloat(claim.claim_paid_amount) || 0);
        const daysOld = daysBetween(claim.service_date || claim.created_at, today);
        const patientName = claim.patients
          ? `${claim.patients.first_name || ''} ${claim.patients.last_name || ''}`.trim()
          : 'Unknown';

        return {
          patient: patientName,
          claim_id: claim.claim_number || claim.id?.slice(0, 8),
          payer: claim.payer || 'Unknown',
          service_date: claim.service_date,
          status: claim.claim_status,
          total_amount: parseFloat(claim.claim_amount) || 0,
          paid_amount: parseFloat(claim.claim_paid_amount) || 0,
          outstanding: outstanding,
          days_outstanding: daysOld,
          bucket: daysOld <= 30 ? '0-30' : daysOld <= 60 ? '31-60' : daysOld <= 90 ? '61-90' : '90+'
        };
      });
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 15 }, { wch: 10 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'AR Aging');
    XLSX.writeFile(wb, `AR_Aging_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating AR Aging report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate Denial Analysis Report
 * Groups denied claims by denial reason/code
 */
export const generateDenialAnalysisReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = [
        { denial_code: 'CO-4', reason: 'Procedure code inconsistent with modifier', count: 45, total_amount: 15750, avg_amount: 350 },
        { denial_code: 'CO-16', reason: 'Claim lacks information', count: 38, total_amount: 16150, avg_amount: 425 },
        { denial_code: 'CO-97', reason: 'Benefit for this service already paid', count: 32, total_amount: 8960, avg_amount: 280 },
        { denial_code: 'CO-29', reason: 'Time limit for filing expired', count: 28, total_amount: 14560, avg_amount: 520 },
        { denial_code: 'CO-15', reason: 'Authorization required', count: 25, total_amount: 17000, avg_amount: 680 },
        { denial_code: 'OTHER', reason: 'Various other reasons', count: 30, total_amount: 9300, avg_amount: 310 },
      ];
    } else if (useAWS) {
      // AWS mode: Get denied claims from Lambda API
      const result = await awsApi.getClaims(practiceId, { limit: 1000, status: 'Denied' });
      const deniedClaims = result.success ? result.data || [] : [];

      // Group by denial reason
      const grouped = {};
      deniedClaims.forEach(claim => {
        const key = claim.denial_code || claim.denial_reason || 'Unspecified';
        if (!grouped[key]) {
          grouped[key] = {
            denial_code: claim.denial_code || 'N/A',
            reason: claim.denial_reason || key,
            count: 0,
            total_amount: 0
          };
        }
        grouped[key].count++;
        grouped[key].total_amount += parseFloat(claim.claim_amount) || 0;
      });

      reportData = Object.values(grouped).map(g => ({
        ...g,
        avg_amount: g.count > 0 ? (g.total_amount / g.count).toFixed(2) : 0
      })).sort((a, b) => b.count - a.count);
    } else {
      // Supabase mode: Get denied claims grouped by denial reason
      const { data: deniedClaims, error } = await supabase
        .from('claims')
        .select('denial_code, denial_reason, claim_amount')
        .eq('practice_id', practiceId)
        .eq('claim_status', 'Denied');

      if (error) throw error;

      // Group by denial reason
      const grouped = {};
      (deniedClaims || []).forEach(claim => {
        const key = claim.denial_code || claim.denial_reason || 'Unspecified';
        if (!grouped[key]) {
          grouped[key] = {
            denial_code: claim.denial_code || 'N/A',
            reason: claim.denial_reason || key,
            count: 0,
            total_amount: 0
          };
        }
        grouped[key].count++;
        grouped[key].total_amount += parseFloat(claim.claim_amount) || 0;
      });

      reportData = Object.values(grouped).map(g => ({
        ...g,
        avg_amount: g.count > 0 ? (g.total_amount / g.count).toFixed(2) : 0
      })).sort((a, b) => b.count - a.count);
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 12 }, { wch: 35 }, { wch: 10 }, { wch: 15 }, { wch: 12 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Denial Analysis');
    XLSX.writeFile(wb, `Denial_Analysis_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating Denial Analysis:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate Payer Performance Summary
 * Shows claims, denial rates, and payment performance by payer
 */
export const generatePayerPerformanceReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = demoPayerPerformance.map(payer => ({
        payer: payer.payer,
        total_claims: payer.claims,
        paid_claims: Math.round(payer.claims * 0.85),
        denied_claims: Math.round(payer.claims * 0.08),
        denial_rate: payer.denialRate,
        avg_days_to_pay: payer.avgDays,
        total_billed: payer.amount,
        total_collected: (parseFloat(payer.amount.replace(/[$,]/g, '')) * 0.92).toFixed(2)
      }));
    } else if (useAWS) {
      // AWS mode: Get claims from Lambda API
      const result = await awsApi.getClaims(practiceId, { limit: 1000 });
      const claims = result.success ? result.data || [] : [];

      // Group by payer
      const grouped = {};
      claims.forEach(claim => {
        const payer = claim.payer_name || claim.payer || 'Unknown';
        if (!grouped[payer]) {
          grouped[payer] = {
            payer,
            total_claims: 0,
            paid_claims: 0,
            denied_claims: 0,
            total_billed: 0,
            total_collected: 0,
            days_to_pay: []
          };
        }
        grouped[payer].total_claims++;
        grouped[payer].total_billed += parseFloat(claim.claim_amount) || 0;
        grouped[payer].total_collected += parseFloat(claim.claim_paid_amount) || 0;

        if (claim.claim_status === 'Paid') {
          grouped[payer].paid_claims++;
          if (claim.service_date && claim.paid_date) {
            grouped[payer].days_to_pay.push(daysBetween(claim.service_date, claim.paid_date));
          }
        }
        if (claim.claim_status === 'Denied') {
          grouped[payer].denied_claims++;
        }
      });

      reportData = Object.values(grouped).map(g => ({
        payer: g.payer,
        total_claims: g.total_claims,
        paid_claims: g.paid_claims,
        denied_claims: g.denied_claims,
        denial_rate: g.total_claims > 0 ? ((g.denied_claims / g.total_claims) * 100).toFixed(1) + '%' : '0%',
        avg_days_to_pay: g.days_to_pay.length > 0
          ? Math.round(g.days_to_pay.reduce((a, b) => a + b, 0) / g.days_to_pay.length)
          : 'N/A',
        total_billed: g.total_billed.toFixed(2),
        total_collected: g.total_collected.toFixed(2),
        collection_rate: g.total_billed > 0 ? ((g.total_collected / g.total_billed) * 100).toFixed(1) + '%' : '0%'
      })).sort((a, b) => b.total_claims - a.total_claims);
    } else {
      // Supabase mode: Get claims grouped by payer
      const { data: claims, error } = await supabase
        .from('claims')
        .select('payer, claim_status, claim_amount, claim_paid_amount, service_date, paid_date')
        .eq('practice_id', practiceId);

      if (error) throw error;

      // Group by payer
      const grouped = {};
      (claims || []).forEach(claim => {
        const payer = claim.payer || 'Unknown';
        if (!grouped[payer]) {
          grouped[payer] = {
            payer,
            total_claims: 0,
            paid_claims: 0,
            denied_claims: 0,
            total_billed: 0,
            total_collected: 0,
            days_to_pay: []
          };
        }
        grouped[payer].total_claims++;
        grouped[payer].total_billed += parseFloat(claim.claim_amount) || 0;
        grouped[payer].total_collected += parseFloat(claim.claim_paid_amount) || 0;

        if (claim.claim_status === 'Paid') {
          grouped[payer].paid_claims++;
          if (claim.service_date && claim.paid_date) {
            grouped[payer].days_to_pay.push(daysBetween(claim.service_date, claim.paid_date));
          }
        }
        if (claim.claim_status === 'Denied') {
          grouped[payer].denied_claims++;
        }
      });

      reportData = Object.values(grouped).map(g => ({
        payer: g.payer,
        total_claims: g.total_claims,
        paid_claims: g.paid_claims,
        denied_claims: g.denied_claims,
        denial_rate: g.total_claims > 0 ? ((g.denied_claims / g.total_claims) * 100).toFixed(1) + '%' : '0%',
        avg_days_to_pay: g.days_to_pay.length > 0
          ? Math.round(g.days_to_pay.reduce((a, b) => a + b, 0) / g.days_to_pay.length)
          : 'N/A',
        total_billed: g.total_billed.toFixed(2),
        total_collected: g.total_collected.toFixed(2),
        collection_rate: g.total_billed > 0 ? ((g.total_collected / g.total_billed) * 100).toFixed(1) + '%' : '0%'
      })).sort((a, b) => b.total_claims - a.total_claims);
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Payer Performance');
    XLSX.writeFile(wb, `Payer_Performance_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating Payer Performance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate Production Report
 * Shows claims by date with amounts
 */
export const generateProductionReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = [
        { date: '2026-01-01', claims_count: 12, total_billed: 8500, total_collected: 7200, adjustments: 650 },
        { date: '2026-01-02', claims_count: 15, total_billed: 9800, total_collected: 8500, adjustments: 720 },
        { date: '2026-01-03', claims_count: 10, total_billed: 6500, total_collected: 5800, adjustments: 420 },
        { date: '2026-01-06', claims_count: 14, total_billed: 10200, total_collected: 9100, adjustments: 850 },
        { date: '2026-01-07', claims_count: 11, total_billed: 7200, total_collected: 6400, adjustments: 520 },
      ];
    } else if (useAWS) {
      // AWS mode: Get claims from Lambda API
      const result = await awsApi.getClaims(practiceId, { limit: 1000 });
      const claims = result.success ? result.data || [] : [];

      // Group by date
      const grouped = {};
      claims.forEach(claim => {
        const date = claim.service_date || 'Unknown';
        if (!grouped[date]) {
          grouped[date] = {
            date,
            claims_count: 0,
            total_billed: 0,
            total_collected: 0
          };
        }
        grouped[date].claims_count++;
        grouped[date].total_billed += parseFloat(claim.claim_amount) || 0;
        grouped[date].total_collected += parseFloat(claim.claim_paid_amount) || 0;
      });

      reportData = Object.values(grouped).map(g => ({
        ...g,
        total_billed: g.total_billed.toFixed(2),
        total_collected: g.total_collected.toFixed(2),
        adjustments: (g.total_billed - g.total_collected).toFixed(2)
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
    } else {
      // Supabase mode: Get claims grouped by service date
      const { data: claims, error } = await supabase
        .from('claims')
        .select('service_date, claim_amount, claim_paid_amount')
        .eq('practice_id', practiceId)
        .order('service_date', { ascending: false });

      if (error) throw error;

      // Group by date
      const grouped = {};
      (claims || []).forEach(claim => {
        const date = claim.service_date || 'Unknown';
        if (!grouped[date]) {
          grouped[date] = {
            date,
            claims_count: 0,
            total_billed: 0,
            total_collected: 0
          };
        }
        grouped[date].claims_count++;
        grouped[date].total_billed += parseFloat(claim.claim_amount) || 0;
        grouped[date].total_collected += parseFloat(claim.claim_paid_amount) || 0;
      });

      reportData = Object.values(grouped).map(g => ({
        ...g,
        total_billed: g.total_billed.toFixed(2),
        total_collected: g.total_collected.toFixed(2),
        adjustments: (g.total_billed - g.total_collected).toFixed(2)
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Production');
    XLSX.writeFile(wb, `Production_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating Production Report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate Collection Report
 * Shows paid claims with payment details
 */
export const generateCollectionReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = [
        { paid_date: '2026-01-25', patient: 'John Smith', claim_id: 'CLM-001', payer: 'Delta Dental', billed: 1250, collected: 1125, status: 'Paid' },
        { paid_date: '2026-01-24', patient: 'Jane Doe', claim_id: 'CLM-002', payer: 'Cigna', billed: 850, collected: 850, status: 'Paid' },
        { paid_date: '2026-01-23', patient: 'Bob Johnson', claim_id: 'CLM-003', payer: 'Aetna', billed: 1200, collected: 1080, status: 'Paid' },
        { paid_date: '2026-01-22', patient: 'Mary Wilson', claim_id: 'CLM-004', payer: 'MetLife', billed: 350, collected: 350, status: 'Paid' },
        { paid_date: '2026-01-21', patient: 'Tom Brown', claim_id: 'CLM-005', payer: 'United', billed: 925, collected: 832, status: 'Paid' },
      ];
    } else if (useAWS) {
      // AWS mode: Get paid claims from Lambda API
      const result = await awsApi.getClaims(practiceId, { limit: 1000, status: 'Paid' });
      const claims = result.success ? result.data || [] : [];

      reportData = claims.map(claim => {
        const patientName = claim.patient_first_name && claim.patient_last_name
          ? `${claim.patient_first_name} ${claim.patient_last_name}`
          : claim.patient_name || 'Unknown';
        return {
          paid_date: claim.paid_date || 'N/A',
          patient: patientName,
          claim_id: claim.claim_number || claim.id?.slice(0, 8),
          payer: claim.payer_name || claim.payer || 'Unknown',
          billed: parseFloat(claim.claim_amount) || 0,
          collected: parseFloat(claim.claim_paid_amount) || 0,
          adjustment: ((parseFloat(claim.claim_amount) || 0) - (parseFloat(claim.claim_paid_amount) || 0)).toFixed(2)
        };
      });
    } else {
      // Supabase mode: Get paid claims
      const { data: claims, error } = await supabase
        .from('claims')
        .select(`
          id,
          claim_number,
          paid_date,
          payer,
          claim_amount,
          claim_paid_amount,
          claim_status,
          patients (first_name, last_name)
        `)
        .eq('practice_id', practiceId)
        .eq('claim_status', 'Paid')
        .order('paid_date', { ascending: false });

      if (error) throw error;

      reportData = (claims || []).map(claim => {
        const patientName = claim.patients
          ? `${claim.patients.first_name || ''} ${claim.patients.last_name || ''}`.trim()
          : 'Unknown';

        return {
          paid_date: claim.paid_date || 'N/A',
          patient: patientName,
          claim_id: claim.claim_number || claim.id?.slice(0, 8),
          payer: claim.payer || 'Unknown',
          billed: parseFloat(claim.claim_amount) || 0,
          collected: parseFloat(claim.claim_paid_amount) || 0,
          adjustment: ((parseFloat(claim.claim_amount) || 0) - (parseFloat(claim.claim_paid_amount) || 0)).toFixed(2)
        };
      });
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Collections');
    XLSX.writeFile(wb, `Collection_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating Collection Report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate Workflow Activity Report
 * Shows AI workflow executions from audit logs
 */
export const generateWorkflowActivityReport = async (practiceId, appMode) => {
  try {
    let reportData;

    if (appMode === 'demo') {
      reportData = [
        { date: '2026-01-29', workflow: 'Benefits Verification', action: 'workflow_execution', count: 45, status: 'Success' },
        { date: '2026-01-29', workflow: 'Claim Validator', action: 'workflow_execution', count: 32, status: 'Success' },
        { date: '2026-01-29', workflow: 'AI Denial Recovery', action: 'workflow_execution', count: 18, status: 'Success' },
        { date: '2026-01-28', workflow: 'Benefits Verification', action: 'workflow_execution', count: 52, status: 'Success' },
        { date: '2026-01-28', workflow: 'Smart Pre-Authorization', action: 'workflow_execution', count: 12, status: 'Success' },
      ];
    } else if (useAWS) {
      // AWS mode: Get audit logs from Lambda API (if endpoint exists)
      try {
        const result = await awsApi.getAuditLogs(practiceId, { limit: 500 });
        const logs = result.success ? (result.logs || []).filter(l => l.action?.startsWith('workflow_')) : [];

        reportData = logs.map(log => ({
          date: log.created_at ? new Date(log.created_at).toISOString().split('T')[0] : 'N/A',
          timestamp: log.created_at,
          action: log.action,
          workflow: log.metadata?.workflow_name || log.action.replace('workflow_', ''),
          status: log.metadata?.result_summary || 'Completed',
          details: JSON.stringify(log.metadata || {})
        }));
      } catch (err) {
        // Audit logs endpoint may not exist yet - return empty
        console.log('Audit logs endpoint not available:', err.message);
        reportData = [];
      }
    } else {
      // Supabase mode: Real data from audit_logs
      const { data: logs, error } = await supabase
        .from('audit_logs')
        .select('created_at, action, metadata')
        .eq('practice_id', practiceId)
        .like('action', 'workflow_%')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      reportData = (logs || []).map(log => ({
        date: log.created_at ? new Date(log.created_at).toISOString().split('T')[0] : 'N/A',
        timestamp: log.created_at,
        action: log.action,
        workflow: log.metadata?.workflow_name || log.action.replace('workflow_', ''),
        status: log.metadata?.result_summary || 'Unknown',
        details: JSON.stringify(log.metadata || {})
      }));
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(reportData);

    ws['!cols'] = [
      { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 40 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Workflow Activity');
    XLSX.writeFile(wb, `Workflow_Activity_${new Date().toISOString().split('T')[0]}.xlsx`);

    return { success: true, recordCount: reportData.length };
  } catch (error) {
    console.error('Error generating Workflow Activity:', error);
    return { success: false, error: error.message };
  }
};
